/**
 * 工單轉倉配置模式：依目標倉彙總 vs 指定工單重寫需求量。
 */

export const TRANSFER_CONFIG_MODE = {
  BY_TARGET_WAREHOUSE: 'byTargetWarehouse',
  BY_WORK_ORDER: 'byWorkOrder',
};

/**
 * @param {string|null|undefined} id
 * @returns {string}
 */
function toIdString(id) {
  if (id == null || id === '') {
    return '';
  }
  return String(id);
}

/**
 * 從 candidates materials[].workOrders[] 去重收集工單（供 dialog 單選）。
 * @param {Array<{ workOrders?: object[] }>} materials
 * @param {{ workWarehouseId?: string|null }} [options]
 * @returns {{ id: string, workOrderNo: string, workWarehouseId: string|null }[]}
 */
export function collectWorkOrdersFromMaterials(materials = [], {
  workWarehouseId = null,
} = {}) {
  const fallbackWhId = toIdString(workWarehouseId) || null;
  const byId = new Map();

  (materials ?? []).forEach((material) => {
    (material?.workOrders ?? []).forEach((wo) => {
      const id = toIdString(wo?.id);
      if (!id || byId.has(id)) {
        return;
      }
      const fromWo = toIdString(wo?.workWarehouseId)
        || toIdString(wo?.workWarehouse?.id)
        || '';
      byId.set(id, {
        id,
        workOrderNo: wo?.workOrderNo == null ? '' : String(wo.workOrderNo),
        workWarehouseId: fromWo || fallbackWhId,
      });
    });
  });

  return Array.from(byId.values()).sort((a, b) => (
    String(a.workOrderNo).localeCompare(String(b.workOrderNo), 'zh-Hant', {
      numeric: true,
    })
  ));
}

/**
 * 以指定工單需求重寫 requiredQty；無該工單需求（或缺／≤0）的列隱藏。
 * @param {object[]} materials
 * @param {string|null|undefined} workOrderId
 * @returns {object[]}
 */
export function applyWorkOrderDemand(materials = [], workOrderId) {
  const woId = toIdString(workOrderId);
  if (!woId) {
    return [];
  }

  const next = [];
  (materials ?? []).forEach((material) => {
    const entry = (material?.workOrders ?? []).find(
      (wo) => toIdString(wo?.id) === woId,
    );
    if (!entry) {
      return;
    }
    const qtyNum = Number(entry.requiredQty);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      return;
    }
    next.push({
      ...material,
      requiredQty: entry.requiredQty,
    });
  });
  return next;
}

/**
 * 選庫／掃描排除倉：目標倉 ＋ 指定工單工作倉（去重）。
 * @param {{
 *   targetWarehouseId?: string|null,
 *   selectedWorkOrder?: {
 *     workWarehouseId?: string|null,
 *     workWarehouse?: { id?: string|null },
 *   }|null,
 * }} [options]
 * @returns {string[]}
 */
export function resolveExcludeWarehouseIds({
  targetWarehouseId = null,
  selectedWorkOrder = null,
} = {}) {
  const ids = [];
  const seen = new Set();

  function add(raw) {
    const id = toIdString(raw);
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    ids.push(id);
  }

  add(targetWarehouseId);
  add(selectedWorkOrder?.workWarehouseId);
  add(selectedWorkOrder?.workWarehouse?.id);
  return ids;
}

/**
 * 合併單一／多個 exclude warehouse id。
 * @param {string|null|undefined} excludeWarehouseId
 * @param {string[]|null|undefined} excludeWarehouseIds
 * @returns {Set<string>}
 */
export function buildExcludeWarehouseIdSet(
  excludeWarehouseId = null,
  excludeWarehouseIds = null,
) {
  const set = new Set();
  const single = toIdString(excludeWarehouseId);
  if (single) {
    set.add(single);
  }
  (excludeWarehouseIds ?? []).forEach((id) => {
    const next = toIdString(id);
    if (next) {
      set.add(next);
    }
  });
  return set;
}
